/* eslint-disable no-param-reassign */
/* eslint-disable no-console */
import { useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import i18n from "i18next";
import { useTranslation } from "react-i18next";

import { Field, formValueSelector, reduxForm } from "redux-form";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import FormField from "components/FormField";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import UploadImageInput from "components/UploadImageInput/UploadImageInput";

import { newAlertMessage } from "store/actions/globalAlertActions";
import { uploadProjectImage, deleteProjectImage } from "store/actions/mainUserActions";

const validate = (values) => {
  const errors = {};

  const required = ["name"];

  required.forEach((f) => {
    if (values[f] === undefined || values[f] === null || values[f] === "") {
      errors[f] = i18n.t("Required");
    }
  });

  return errors;
};

function ServiceCategoryForm(props) {
  const {
    handleSubmit,
    pristine,
    onSubmit,
    actionType,
    projectId,
    mainUserUid,
    existingImageUid,
    formImageUid,
    change,
    actNewAlertMessage,
    actUploadProjectImage,
    actDeleteProjectImage,
  } = props;

  const { t } = useTranslation();

  const [imageData, setImageData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isImageMarkedForDelete, setIsImageMarkedForDelete] = useState(false);

  const buildCleanedData = (data, imageUidFinal) => ({
    ...data,
    imageUid: imageUidFinal || null,
  });

  const handleSave = (data) => {
    if (!projectId) {
      actNewAlertMessage({ type: "warning", title: "image", content: "missingProjectId" });
      return;
    }

    setIsSaving(true);

    if (!imageData) {
      if (isImageMarkedForDelete && existingImageUid) {
        actDeleteProjectImage({
          imageUid: existingImageUid,
          projectId,
          cb: (deletedImageUid) => {
            if (deletedImageUid) {
              onSubmit(buildCleanedData(data, null));
              setIsImageMarkedForDelete(false);
            } else {
              actNewAlertMessage({
                type: "error",
                title: "image",
                content: `${t("Delete")} ${t("unknownError")}`,
              });
            }
            setIsSaving(false);
          },
        });
        return;
      }

      const uidToPersist = data?.imageUid || existingImageUid || null;

      onSubmit(buildCleanedData(data, uidToPersist));
      setIsSaving(false);
      return;
    }

    actUploadProjectImage({
      image: imageData,
      projectId,
      cb: (imageUid) => {
        if (imageUid) {
          change("imageUid", imageUid);

          setImageData(null);

          actNewAlertMessage({
            type: "success",
            title: "image",
            content: `${t("uploadFile")} ${t("Success")}`,
          });

          onSubmit(buildCleanedData(data, imageUid));
          setIsSaving(false);
        } else {
          actNewAlertMessage({
            type: "error",
            title: "image",
            content: `${t("uploadFile")} ${t("unknownError")}`,
          });
          setIsSaving(false);
        }
      },
    });
  };

  const canSave =
    !isSaving &&
    (!pristine ||
      Boolean(isImageMarkedForDelete) ||
      Boolean(imageData) ||
      (Boolean(formImageUid) && formImageUid !== existingImageUid));

  return (
    <MDBox py={3}>
      <form onSubmit={handleSubmit(handleSave)}>
        <Grid container justifyContent="center">
          <Card id="basic-info">
            <MDBox
              height="4rem"
              bgColor="info"
              variant="gradient"
              coloredShadow="info"
              borderRadius="xl"
              display="flex"
              justifyContent="center"
              alignItems="center"
              color="white"
              mt={-3}
              mx={10}
              px={2}
            >
              {t(actionType)} {t("serviceCategory")}
            </MDBox>

            <MDBox pt={3} pb={3} px={4}>
              <Grid container spacing={4} justifyContent="center">
                <Field name="imageUid" component="input" type="hidden" />

                {/* NAME */}
                <Grid size={{ xs: 12, md: 8 }}>
                  <Field name="name" component={FormField} label={t("name")} />
                </Grid>

                {/* INFO */}
                <Grid size={{ xs: 12, md: 8 }}>
                  <Field name="info" component={FormField} label={t("additionalInfo")} />
                </Grid>

                {/* FOTO */}
                <Grid size={{ xs: 12, md: 8 }}>
                  <UploadImageInput
                    label={t("image")}
                    helpText={`${t("onlyPng")} · ${t("max")} 1MB`}
                    inputId="service-category-img-file-input"
                    selectLabel={t("Select")}
                    deleteLabel={t("Delete")}
                    uploadPlaceholder={t("uploadFile")}
                    maxBytes={1024 * 1024}
                    projectId={projectId}
                    mainUserUid={mainUserUid}
                    existingImageUid={existingImageUid}
                    formImageUid={formImageUid}
                    onNewAlertMessage={actNewAlertMessage}
                    onSetImageData={(dataUrl) => {
                      setImageData(dataUrl);
                      if (dataUrl) setIsImageMarkedForDelete(false);
                    }}
                    onDeleteImage={(isDeleted) => setIsImageMarkedForDelete(isDeleted)}
                    onChangeImageUid={(uid) => change("imageUid", uid)}
                  />
                </Grid>

                {/* SUBMIT */}
                <Grid size={{ xs: 12 }}>
                  <MDBox display="flex" justifyContent="center" mt={2}>
                    <MDButton
                      type="submit"
                      variant="gradient"
                      color="dark"
                      size="small"
                      disabled={!canSave}
                    >
                      {isSaving ? t("Saving") : t("Save")}
                    </MDButton>
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </Grid>
      </form>
    </MDBox>
  );
}

ServiceCategoryForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  actionType: PropTypes.string.isRequired,
  projectId: PropTypes.string,
  mainUserUid: PropTypes.string,
  existingImageUid: PropTypes.string,
  formImageUid: PropTypes.string,
  change: PropTypes.func.isRequired,
  actNewAlertMessage: PropTypes.func.isRequired,
  actUploadProjectImage: PropTypes.func.isRequired,
  actDeleteProjectImage: PropTypes.func.isRequired,
};

ServiceCategoryForm.defaultProps = {
  projectId: "",
  mainUserUid: "",
  existingImageUid: "",
  formImageUid: "",
};

const selector = formValueSelector("ServiceCategoryForm");

const mapStateToProps = (state, props) => {
  const { actionType, uids, projectId } = props;

  const servicesCategories = state.servicesReducer?.servicesCategories || {};

  let initial = {
    name: "",
    info: "",
    imageUid: null,
  };

  if (actionType === "edit" && uids.length === 1 && servicesCategories[uids[0]]) {
    initial = { ...initial, ...servicesCategories[uids[0]] };
  }

  return {
    initialValues: initial,
    mainUserUid: state?.mainUserReducer?.uid || "",
    existingImageUid: initial?.imageUid || "",
    formImageUid: selector(state, "imageUid") || "",
    projectId,
  };
};

const mapDispatchToProps = (dispatch) => ({
  actNewAlertMessage: (message) => dispatch(newAlertMessage(message)),
  actUploadProjectImage: (data) => dispatch(uploadProjectImage(data)),
  actDeleteProjectImage: (data) => dispatch(deleteProjectImage(data)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(reduxForm({ form: "ServiceCategoryForm", validate })(ServiceCategoryForm));
